import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, serverTimestamp, addDoc, doc, updateDoc, deleteDoc, where, getDocs } from 'firebase/firestore'; 
import { format } from 'date-fns'; 
import { fr } from 'date-fns/locale';
import { UserX, Clock, Calendar, MessageSquare, Loader2, Filter, Edit, Trash2, PlusCircle, CheckCircle, XCircle, Save, AlertTriangle, UserCheck } from 'lucide-react'; 

/**
 * Composant de gestion complète des retards et absences avec Mode Appel (Batch).
 */
const RetardsAbsencesManagement = ({ db, setError, setMessage, schoolId, userRole, userUid }) => {
  // --- Rôles et Permissions ---
  const canManage = ['superAdmin', 'admin', 'directeurPedagogique', 'secretaireGeneral', 'enseignant'].includes(userRole); 
  const canView = canManage; 

  // --- Configuration de l'Année Scolaire Actuelle ---
  const getCurrentSchoolYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    // L'année scolaire commence en Septembre (index 8)
    return currentMonth >= 8 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`; 
  };
  const currentSchoolYear = useMemo(getCurrentSchoolYear, []);

  // --- États des données ---
  const [retardsAbsences, setRetardsAbsences] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]); 
  const [enrollments, setEnrollments] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);

  // --- États du formulaire individuel (Modale 1) ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [recordType, setRecordType] = useState('');
  const [selectedEleveId, setSelectedEleveId] = useState('');
  const [selectedClassForRecord, setSelectedClassForRecord] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd')); 
  const [heure, setHeure] = useState('');
  const [justification, setJustification] = useState('');
  const [actionEntreprise, setActionEntreprise] = useState('');

  // --- NOUVEAUX ÉTATS : MODE APPEL (BATCH) ---
  const [isRollCallModalOpen, setIsRollCallModalOpen] = useState(false);
  const [rollCallClassId, setRollCallClassId] = useState('');
  const [selectedRollCallEleves, setSelectedRollCallEleves] = useState([]); // Liste des IDs cochés
  const [rollCallType, setRollCallType] = useState('absence'); // 'absence' ou 'retard'
  const [rollCallDate, setRollCallDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [rollCallTime, setRollCallTime] = useState('');
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);

  // --- États des filtres ---
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterClasseId, setFilterClasseId] = useState('');
  const [filterEleveId, setFilterEleveId] = useState('');
  const [filterJustified, setFilterJustified] = useState('');
  
  // --- NOUVEAU : États pour les permissions enseignant ---
  const [teacherAssignedClasses, setTeacherAssignedClasses] = useState([]);
  const [loadingTeacherPermissions, setLoadingTeacherPermissions] = useState(false);
  const [teacherId, setTeacherId] = useState(null);
  const teacherDataCache = useRef({
    lastFetch: null,
    data: null
  });

  // --- Refs pour les données brutes ---
  const elevesRawRef = useRef([]);
  const classesRawRef = useRef([]); 
  const enrollmentsRawRef = useRef([]); 

  // --- Fonctions Utilitaires ---
  const formatDateForDisplay = (date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return format(d, includeTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: fr });
  };

  // --- Fonction pour vérifier si l'enseignant peut accéder à une classe ---
  const canAccessClass = useCallback((classId) => {
    if (userRole !== 'enseignant') return true;
    if (!classId || teacherAssignedClasses.length === 0) return false;
    return teacherAssignedClasses.includes(classId);
  }, [userRole, teacherAssignedClasses]);

  // --- Charger les classes affectées à l'enseignant ---
  useEffect(() => {
    const fetchTeacherAssignedClasses = async () => {
      if (!db || !schoolId || !userUid || userRole !== 'enseignant') {
        setTeacherAssignedClasses([]);
        setLoadingTeacherPermissions(false);
        return;
      }

      // Vérifier le cache (5 minutes de cache)
      const now = Date.now();
      if (teacherDataCache.current.lastFetch && 
          teacherDataCache.current.data && 
          (now - teacherDataCache.current.lastFetch < 5 * 60 * 1000)) {
        
        setTeacherAssignedClasses(teacherDataCache.current.data.assignedClasses);
        setTeacherId(teacherDataCache.current.data.teacherId);
        setLoadingTeacherPermissions(false);
        return;
      }
      
      setLoadingTeacherPermissions(true);
      try {
        // Récupérer l'enseignant correspondant à l'utilisateur connecté
        const enseignantsRef = collection(db, 'schools', schoolId, 'enseignants');
        const q = query(enseignantsRef, where('userId', '==', userUid));
        const enseignantSnapshot = await getDocs(q);
        
        if (!enseignantSnapshot.empty) {
          const enseignantDoc = enseignantSnapshot.docs[0];
          const enseignantId = enseignantDoc.id;
          setTeacherId(enseignantId);
          
          // Récupérer les affectations de classes de cet enseignant
          const assignmentsRef = collection(db, 'schools', schoolId, 'teacherClassAssignments');
          const assignmentsQuery = query(
            assignmentsRef, 
            where('teacherId', '==', enseignantId)
          );
          const assignmentsSnapshot = await getDocs(assignmentsQuery);
          const assignedClasses = assignmentsSnapshot.docs.map(doc => doc.data().classId);
          
          // Mettre en cache les données
          teacherDataCache.current = {
            lastFetch: now,
            data: {
              teacherId: enseignantId,
              assignedClasses: assignedClasses,
              timestamp: now
            }
          };

          setTeacherAssignedClasses(assignedClasses);
          
          if (assignedClasses.length === 0) {
            setMessage("Vous n'êtes actuellement affecté à aucune classe. Contactez l'administration.");
          }
        } else {
          setMessage("Vous n'êtes pas enregistré comme enseignant dans cette école. Contactez l'administration.");
          setTeacherAssignedClasses([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des classes de l'enseignant:", error);
        setError("Erreur lors du chargement de vos classes affectées.");
        setTeacherAssignedClasses([]);
      } finally {
        setLoadingTeacherPermissions(false);
      }
    };
    
    fetchTeacherAssignedClasses();
  }, [db, schoolId, userUid, userRole, setMessage, setError]);

  // --- CHARGEMENT DES DONNÉES FIREBASE ---
  useEffect(() => {
    if (!db || !schoolId || !canView) {
      setIsLoading(false);
      return;
    }
    setError('');
    setMessage('');

    const recordsCollectionRef = collection(db, 'schools', schoolId, 'absencesRetards');
    const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
    const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
    const enrollmentsCollectionRef = collection(db, 'schools', schoolId, 'enrollments'); 

    const unsubscribes = [];

    // 1. Classes
    unsubscribes.push(onSnapshot(classesCollectionRef, (snapshot) => {
      classesRawRef.current = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClasses(classesRawRef.current); 
    }, (error) => setError(`Erreur chargement classes: ${error.message}`)));

    // 2. Eleves (bruts) 
    unsubscribes.push(onSnapshot(elevesCollectionRef, (snapshot) => {
      elevesRawRef.current = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }, (error) => setError(`Erreur chargement élèves bruts: ${error.message}`)));

    // 3. Enrollments pour l'année scolaire actuelle
   const qEnrollments = query(
    enrollmentsCollectionRef, 
    where('schoolYear', '==', currentSchoolYear)
    );
    unsubscribes.push(onSnapshot(qEnrollments, (snapshot) => {
      enrollmentsRawRef.current = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEnrollments(enrollmentsRawRef.current); 
    }, (error) => setError(`Erreur chargement inscriptions: ${error.message}`)));

    // 4. Retards/Absences (filtrés par l'année scolaire en cours)
    const qRecords = query(
      recordsCollectionRef,
      where('schoolYear', '==', currentSchoolYear),
      orderBy("date", "desc")
    );
    unsubscribes.push(onSnapshot(qRecords, (snapshot) => {
      const recordsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRetardsAbsences(recordsData);
    }, (error) => setError(`Erreur chargement des suivis: ${error.message}`)));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [db, schoolId, setError, setMessage, canView, currentSchoolYear]);

  // --- LOGIQUE D'ENRICHISSEMENT DES ÉLÈVES ---
  useEffect(() => {
    if (
        isLoading && 
        elevesRawRef.current.length === 0 &&
        classesRawRef.current.length === 0 &&
        enrollmentsRawRef.current.length === 0
    ) {
        return;
    }

    const enrichedElevesList = elevesRawRef.current
        .map(e => {
            const studentEnrollment = enrollmentsRawRef.current.find(
                enroll => enroll.studentId === e.id && enroll.schoolYear === currentSchoolYear
            );

            let studentClassName = 'N/A';
            let studentClasseId = null;
            let studentAuthUID = null; 

            if (studentEnrollment && studentEnrollment.classId) {
                const classe = classesRawRef.current.find(c => c.id === studentEnrollment.classId);
                if (classe) {
                    studentClassName = classe.nom;
                    studentClasseId = classe.id;
                }
                studentAuthUID = studentEnrollment.studentAuthUID;
            }

            return {
                ...e,
                className: studentClassName,
                classeId: studentClasseId,
                studentAuthUID: studentAuthUID,
            };
        })
        .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));

    setEleves(enrichedElevesList);
    setIsLoading(false);
  }, [elevesRawRef.current, classesRawRef.current, enrollmentsRawRef.current, currentSchoolYear, isLoading]);


  // --- LOGIQUE CRUD INDIVIDUEL (Add/Edit/Delete) ---

  const resetForm = () => {
    setIsEditing(false);
    setCurrentRecordId(null);
    setSelectedEleveId('');
    setSelectedClassForRecord(''); 
    setRecordType('');
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setHeure('');
    setJustification('');
    setActionEntreprise('');
    setIsModalOpen(false);
  };

  const handleEdit = (record) => {
    // Vérifier que l'enseignant a accès à cette note
    if (userRole === 'enseignant' && !canAccessClass(record.classeId)) {
      setError("Vous n'êtes pas autorisé à modifier cet enregistrement.");
      return;
    }

    setCurrentRecordId(record.id);
    setIsEditing(true);

    let studentFirestoreId = '';
    let foundStudent = null;
    
    foundStudent = eleves.find(e => e.id === record.eleveId);
    if (foundStudent) {
        studentFirestoreId = foundStudent.id;
    } else {
        foundStudent = eleves.find(e => e.studentAuthUID === record.eleveId);
        if (foundStudent) {
            studentFirestoreId = foundStudent.id;
        } else {
            console.warn("Élève non trouvé pour l'enregistrement lors de l'édition.", record.eleveId);
        }
    }

    setSelectedEleveId(studentFirestoreId);
    setSelectedClassForRecord(foundStudent ? (foundStudent.classeId || '') : '');
    
    setRecordType(record.type);
    setDate(format(record.date.toDate(), 'yyyy-MM-dd'));
    setHeure(record.heure || '');
    setJustification(record.justification || '');
    setActionEntreprise(record.actionEntreprise || '');
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!canManage) {
        setError("Action non autorisée.");
        return;
    }

    const selectedEleve = eleves.find(e => e.id === selectedEleveId);
    if (!selectedEleveId || !recordType || !date) {
      setError("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    if (!selectedEleve || !selectedEleve.classeId || selectedEleve.className === 'N/A') {
        setError(`L'élève sélectionné n'a pas de classe assignée pour l'année scolaire actuelle.`);
        return;
    }

    // Vérifier que l'enseignant a accès à cette classe
    if (userRole === 'enseignant' && !canAccessClass(selectedEleve.classeId)) {
      setError("Vous n'êtes pas autorisé à gérer les retards/absences pour cette classe.");
      return;
    }

    let recordDate = new Date(date);
    if (recordType === 'retard' && heure) {
      const [hours, minutes] = heure.split(':');
      recordDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    } else {
        recordDate.setHours(0, 0, 0, 0);
    }
    
    const dataToSave = {
        eleveId: selectedEleve.id, 
        eleveName: selectedEleve.name, 
        classeId: selectedEleve.classeId, 
        className: selectedEleve.className, 
        schoolYear: currentSchoolYear,
        schoolId: schoolId,
        type: recordType, 
        date: recordDate, 
        heure: recordType === 'retard' ? heure : '',
        justification: justification,
        actionEntreprise: actionEntreprise,
        isJustified: justification.trim().length > 0, 
        status: 'pending', 
        updatedAt: serverTimestamp(),
        reportedBy: userUid
    };

    try {
      if (isEditing) {
        const docRef = doc(db, 'schools', schoolId, 'absencesRetards', currentRecordId);
        await updateDoc(docRef, dataToSave);
        setMessage(`Suivi de ${recordType} mis à jour avec succès.`);
      } else {
        await addDoc(collection(db, 'schools', schoolId, 'absencesRetards'), {
          ...dataToSave,
          createdAt: serverTimestamp(),
        });
        setMessage(`Suivi de ${recordType} enregistré avec succès.`);
      }
      resetForm();
    } catch (error) {
      setError(`Échec de l'enregistrement: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!canManage) {
        setError("Action non autorisée.");
        return;
    }
    
    // Récupérer l'enregistrement pour vérifier la permission
    const recordToDelete = retardsAbsences.find(r => r.id === id);
    if (!recordToDelete) return;
    
    // Vérifier que l'enseignant a accès à cette classe
    if (userRole === 'enseignant' && !canAccessClass(recordToDelete.classeId)) {
      setError("Vous n'êtes pas autorisé à supprimer cet enregistrement.");
      return;
    }
    
    if (window.confirm("Êtes-vous sûr de vouloir supprimer définitivement ce suivi ?")) {
      try {
        await deleteDoc(doc(db, 'schools', schoolId, 'absencesRetards', id));
        setMessage("Suivi supprimé avec succès.");
      } catch (error) {
        setError(`Échec de la suppression: ${error.message}`);
      }
    }
  };

  // --- LOGIQUE DU MODE APPEL (BATCH SAVE) ---

  const toggleRollCallEleve = (eleveId) => {
    setSelectedRollCallEleves(prev => {
      if (prev.includes(eleveId)) {
        return prev.filter(id => id !== eleveId);
      } else {
        return [...prev, eleveId];
      }
    });
  };

  const handleBatchSave = async () => {
    if (selectedRollCallEleves.length === 0) {
      setError("Veuillez sélectionner au moins un élève.");
      return;
    }
    if (!rollCallClassId) {
        setError("Veuillez sélectionner une classe.");
        return;
    }

    // Vérifier que l'enseignant a accès à cette classe
    if (userRole === 'enseignant' && !canAccessClass(rollCallClassId)) {
      setError("Vous n'êtes pas autorisé à gérer les retards/absences pour cette classe.");
      return;
    }

    setIsSubmittingBatch(true);
    setError('');
    setMessage('');

    try {
      // Préparation des promesses pour envoi parallèle
      const batchPromises = selectedRollCallEleves.map(async (eleveId) => {
        const selectedEleve = eleves.find(e => e.id === eleveId);
        if (!selectedEleve) return null;

        let recordDate = new Date(rollCallDate);
        if (rollCallType === 'retard' && rollCallTime) {
            const [hours, minutes] = rollCallTime.split(':');
            recordDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
        } else {
            recordDate.setHours(0, 0, 0, 0);
        }

        const dataToSave = {
            eleveId: selectedEleve.id,
            eleveName: selectedEleve.name,
            classeId: selectedEleve.classeId,
            className: selectedEleve.className,
            schoolYear: currentSchoolYear,
            schoolId: schoolId,
            type: rollCallType, // 'absence' ou 'retard'
            date: recordDate,
            heure: rollCallType === 'retard' ? rollCallTime : '',
            justification: '',
            actionEntreprise: '',
            isJustified: false,
            status: 'pending',
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            reportedBy: userUid
        };

        return addDoc(collection(db, 'schools', schoolId, 'absencesRetards'), dataToSave);
      });

      await Promise.all(batchPromises);
      
      setMessage(`${selectedRollCallEleves.length} enregistrements ajoutés avec succès.`);
      setIsRollCallModalOpen(false);
      setSelectedRollCallEleves([]);
      setRollCallClassId('');
    } catch (err) {
      console.error(err);
      setError("Erreur lors de l'enregistrement de l'appel : " + err.message);
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  // --- FILTRAGE ET TRI ---
  const filteredRecords = useMemo(() => {
    let filtered = retardsAbsences;
    
    // Pour les enseignants, filtrer d'abord par leurs classes
    if (userRole === 'enseignant' && teacherAssignedClasses.length > 0) {
      filtered = filtered.filter(record => 
        teacherAssignedClasses.includes(record.classeId)
      );
    }
    
    // Appliquer les autres filtres
    return filtered
      .filter(record => {
        if (filterType && record.type !== filterType) return false;
        if (filterClasseId && record.classeId !== filterClasseId) return false;
        
        if (filterEleveId) {
            const selectedEleveForFilter = eleves.find(e => e.id === filterEleveId);
            if (!selectedEleveForFilter) return false; 
            if (record.eleveId !== selectedEleveForFilter.id && record.eleveId !== selectedEleveForFilter.studentAuthUID) {
                return false;
            }
        }

        if (filterJustified === 'justified' && !record.isJustified) return false;
        if (filterJustified === 'unjustified' && record.isJustified) return false;
        
        return true;
      });
  }, [retardsAbsences, filterType, filterClasseId, filterEleveId, filterJustified, eleves, userRole, teacherAssignedClasses]);

  // --- RENDU : MODALE APPEL (ROLL CALL) ---
  const renderRollCallModal = () => {
    if (!isRollCallModalOpen) return null;

    // Filtrer les élèves de la classe sélectionnée
    const classStudents = eleves
        .filter(e => e.classeId === rollCallClassId)
        .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 flex flex-col max-h-[90vh]">
          
          {/* Header */}
          <div className="flex justify-between items-center pb-3 border-b">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <CheckCircle className="mr-2 text-indigo-600" /> Faire l'Appel / Saisie Rapide
            </h3>
            <button onClick={() => setIsRollCallModalOpen(false)} className="text-gray-400 hover:text-gray-600">
              <XCircle size={24} />
            </button>
          </div>

          {/* Configuration */}
          <div className="py-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-b bg-gray-50 p-4 rounded-md mb-2">
             {/* 1. Sélection Classe */}
             <div>
                <label className="block text-sm font-medium text-gray-700">Classe</label>
                <select
                    value={rollCallClassId}
                    onChange={(e) => {
                        setRollCallClassId(e.target.value);
                        setSelectedRollCallEleves([]);
                    }}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={loadingTeacherPermissions}
                >
                    <option value="">Choisir une classe...</option>
                    {userRole === 'enseignant' ? (
                      loadingTeacherPermissions ? (
                        <option value="" disabled>Chargement de vos classes...</option>
                      ) : teacherAssignedClasses.length === 0 ? (
                        <option value="" disabled>Aucune classe attribuée</option>
                      ) : (
                        classes
                          .filter(c => teacherAssignedClasses.includes(c.id))
                          .map(c => (
                            <option key={c.id} value={c.id}>{c.nom}</option>
                          ))
                      )
                    ) : (
                      classes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))
                    )}
                </select>
             </div>

             {/* 2. Date */}
             <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <input
                    type="date"
                    value={rollCallDate}
                    onChange={(e) => setRollCallDate(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                />
             </div>

             {/* 3. Type */}
             <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <div className="mt-2 flex space-x-4">
                    <label className="inline-flex items-center cursor-pointer">
                        <input type="radio" name="rollCallType" checked={rollCallType === 'absence'} onChange={() => setRollCallType('absence')} className="text-red-600 focus:ring-red-500"/>
                        <span className="ml-2 text-sm font-medium text-gray-700">Absence</span>
                    </label>
                    <label className="inline-flex items-center cursor-pointer">
                        <input type="radio" name="rollCallType" checked={rollCallType === 'retard'} onChange={() => setRollCallType('retard')} className="text-yellow-600 focus:ring-yellow-500"/>
                        <span className="ml-2 text-sm font-medium text-gray-700">Retard</span>
                    </label>
                </div>
             </div>

             {/* 4. Heure (Si retard) */}
             {rollCallType === 'retard' && (
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Heure d'arrivée</label>
                    <input
                        type="time"
                        value={rollCallTime}
                        onChange={(e) => setRollCallTime(e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                 </div>
             )}
          </div>

          {/* Liste des élèves (Checkboxes) */}
          <div className="flex-1 overflow-y-auto mt-2 p-2 border rounded bg-white">
            {rollCallClassId ? (
                classStudents.length > 0 ? (
                    <div>
                        <p className="text-sm text-gray-500 mb-3 sticky top-0 bg-white pb-2 border-b">
                            Cochez les élèves <b>{rollCallType === 'absence' ? 'absents' : 'en retard'}</b> :
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {classStudents.map(eleve => (
                                <label 
                                    key={eleve.id} 
                                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-150 ${selectedRollCallEleves.includes(eleve.id) ? 'bg-indigo-50 border-indigo-500 shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedRollCallEleves.includes(eleve.id)}
                                        onChange={() => toggleRollCallEleve(eleve.id)}
                                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-900">{eleve.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <UserX size={48} className="mb-2 opacity-20" />
                        <p>Aucun élève trouvé dans cette classe.</p>
                    </div>
                )
            ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <Filter size={48} className="mb-2 opacity-20" />
                    <p>Veuillez sélectionner une classe ci-dessus.</p>
                </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="pt-4 mt-4 border-t flex justify-between items-center bg-white">
             <div className="text-sm font-medium text-gray-600">
                {selectedRollCallEleves.length} élève(s) sélectionné(s)
             </div>
             <div className="flex space-x-3">
                <button
                    type="button"
                    onClick={() => setIsRollCallModalOpen(false)}
                    className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Annuler
                </button>
                <button
                    onClick={handleBatchSave}
                    disabled={isSubmittingBatch || selectedRollCallEleves.length === 0 || (userRole === 'enseignant' && !canAccessClass(rollCallClassId))}
                    className={`py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white flex items-center shadow-sm ${isSubmittingBatch || selectedRollCallEleves.length === 0 || (userRole === 'enseignant' && !canAccessClass(rollCallClassId)) ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                    {isSubmittingBatch ? <Loader2 className="animate-spin mr-2" size={18}/> : <Save size={18} className="mr-2" />}
                    Valider l'Appel
                </button>
             </div>
          </div>

        </div>
      </div>
    );
  };

  // --- RENDU : MODALE INDIVIDUELLE ---
  const renderRecordModal = () => {
    if (!isModalOpen) return null;
    const currentEleve = eleves.find(e => e.id === selectedEleveId);
    
    // Filtrer les élèves selon la classe sélectionnée et les permissions
    let filteredElevesByClass = eleves.filter(e => e.classeId !== null);
    
    if (selectedClassForRecord) {
      filteredElevesByClass = filteredElevesByClass.filter(e => e.classeId === selectedClassForRecord);
    }
    
    // Pour les enseignants, filtrer aussi par leurs classes
    if (userRole === 'enseignant' && teacherAssignedClasses.length > 0) {
      filteredElevesByClass = filteredElevesByClass.filter(e => 
        teacherAssignedClasses.includes(e.classeId)
      );
    }
    
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center pb-3 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Modifier le Suivi' : 'Enregistrer Retard/Absence'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <XCircle size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="mt-4 space-y-4">
              {/* 0. Sélection Classe */}
              <div>
                  <label htmlFor="modalClass" className="block text-sm font-medium text-gray-700">
                      Classe Cible (Filtrer les élèves)
                  </label>
                  <select
                      id="modalClass"
                      value={selectedClassForRecord}
                      onChange={(e) => {
                          setSelectedClassForRecord(e.target.value);
                          setSelectedEleveId(''); 
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                      disabled={isEditing || loadingTeacherPermissions}
                  >
                      <option value="">Sélectionnez une classe</option>
                      {userRole === 'enseignant' ? (
                        loadingTeacherPermissions ? (
                          <option value="" disabled>Chargement de vos classes...</option>
                        ) : teacherAssignedClasses.length === 0 ? (
                          <option value="" disabled>Aucune classe attribuée</option>
                        ) : (
                          classes
                            .filter(c => teacherAssignedClasses.includes(c.id))
                            .map(c => (
                              <option key={c.id} value={c.id}>{c.nom}</option>
                            ))
                        )
                      ) : (
                        classes.map(c => (
                          <option key={c.id} value={c.id}>{c.nom}</option>
                        ))
                      )}
                  </select>
               </div>
              
              {/* 1. Élève */}
              <div>
                <label htmlFor="modalEleve" className="block text-sm font-medium text-gray-700">Élève (Année {currentSchoolYear})</label>
                <select
                  id="modalEleve"
                  value={selectedEleveId}
                  onChange={(e) => setSelectedEleveId(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                  disabled={isEditing || !selectedClassForRecord}
                >
                  <option value="">
                      {selectedClassForRecord ? 'Sélectionnez un élève' : 'Sélectionnez d\'abord une classe'}
                  </option>
                  {filteredElevesByClass.map(eleve => (
                    <option key={eleve.id} value={eleve.id}>
                      {eleve.name} ({eleve.className})
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Type de Suivi</label>
                <div className="mt-1 flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="recordType"
                      value="absence"
                      checked={recordType === 'absence'}
                      onChange={(e) => setRecordType(e.target.value)}
                      className="h-4 w-4 text-red-600 border-gray-300"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-700">Absence</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="recordType"
                      value="retard"
                      checked={recordType === 'retard'}
                      onChange={(e) => setRecordType(e.target.value)}
                      className="h-4 w-4 text-amber-600 border-gray-300"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-700">Retard</span>
                  </label>
                </div>
              </div>

              {/* 3. Date et Heure */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modalDate" className="block text-sm font-medium text-gray-700">Date du Fait</label>
                  <input
                    type="date"
                    id="modalDate"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                    required
                  />
                </div>
                {recordType === 'retard' && (
                   <div>
                    <label htmlFor="modalHeure" className="block text-sm font-medium text-gray-700">Heure du Retard</label>
                    <input
                      type="time"
                      id="modalHeure"
                      value={heure}
                      onChange={(e) => setHeure(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                    />
                 </div>
                )}
              </div>
              
              {/* 4. Justification */}
              <div>
                <label htmlFor="modalJustification" className="block text-sm font-medium text-gray-700">Justification (Optionnel)</label>
                <textarea
                  id="modalJustification"
                  rows="2"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  placeholder="Ex: Certificat médical, mot d'excuse..."
                />
              </div>

              {/* 5. Action Entreprise */}
              <div>
                <label htmlFor="modalActionEntreprise" className="block text-sm font-medium text-gray-700">Action Entreprise (Optionnel)</label>
                <textarea
                  id="modalActionEntreprise"
                  rows="2"
                  value={actionEntreprise}
                  onChange={(e) => setActionEntreprise(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  placeholder="Ex: Contact du parent, avertissement..."
                />
              </div>

              <div className="flex justify-end pt-4 space-x-3 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 flex items-center"
                  disabled={userRole === 'enseignant' && currentEleve && !canAccessClass(currentEleve.classeId)}
                >
                  <Save size={18} className="mr-2" />
                  {isEditing ? 'Sauvegarder' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
    );
  };

  if (!canView) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
        Vous n'avez pas l'autorisation de voir cette section.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-indigo-500" size={48} />
        <p className="ml-3 text-lg text-gray-700">Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <UserX size={28} className="mr-3 text-red-600" /> Gestion des Retards et Absences
      </h2>

      {/* Bannière d'information pour les enseignants */}
      {userRole === 'enseignant' && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center text-blue-800">
            <UserCheck size={18} className="mr-2" />
            <span className="font-medium">Mode Enseignant</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            {loadingTeacherPermissions ? (
              <span className="flex items-center">
                <Loader2 className="animate-spin mr-2" size={14} />
                Chargement de vos classes...
              </span>
            ) : teacherAssignedClasses.length > 0 ? (
              `Vous pouvez gérer les retards/absences pour ${teacherAssignedClasses.length} classe(s)`
            ) : (
              "Vous n'êtes actuellement affecté à aucune classe. Contactez l'administration."
            )}
          </p>
        </div>
      )}

      {/* Boutons d'action et filtres */}
      <div className="flex flex-col md:flex-row justify-between items-start mb-6 space-y-3 md:space-y-0">
        {canManage && (
          <div className="flex space-x-2">
            {/* Vérifier que l'enseignant a au moins une classe assignée */}
            {userRole !== 'enseignant' || teacherAssignedClasses.length > 0 ? (
              <>
                {/* 1. Bouton Nouveau Suivi (Individuel) */}
                <button
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(true);
                  }}
                  className="flex items-center px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-150"
                  disabled={loadingTeacherPermissions}
                >
                  <PlusCircle size={20} className="mr-2" />
                  Nouveau Suivi
                </button>

                {/* 2. Bouton Faire l'Appel (Nouveau) */}
                <button
                  onClick={() => {
                    setSelectedRollCallEleves([]);
                    setRollCallClassId('');
                    setIsRollCallModalOpen(true);
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-150"
                  disabled={loadingTeacherPermissions}
                >
                  <UserCheck size={20} className="mr-2" />
                  Faire l'Appel
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-500 p-2">
                {loadingTeacherPermissions ? 
                  'Chargement des permissions...' : 
                  'Aucune classe assignée. Contactez l\'administration.'}
              </div>
            )}
          </div>
        )}
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center px-4 py-2 border rounded-lg font-semibold transition duration-150 ${showFilters ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
        >
          <Filter size={20} className="mr-2" />
          {showFilters ? 'Masquer' : 'Afficher'} les Filtres
        </button>
      </div>
      
      {/* Zone de Filtrage */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 border rounded-lg bg-gray-50 shadow-inner">
            <div>
                <label htmlFor="filterType" className="block text-sm font-medium text-gray-700">Type</label>
                <select id="filterType" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Tous</option>
                    <option value="absence">Absences</option>
                    <option value="retard">Retards</option>
                </select>
            </div>
            <div>
                <label htmlFor="filterJustified" className="block text-sm font-medium text-gray-700">Statut</label>
                <select id="filterJustified" value={filterJustified} onChange={(e) => setFilterJustified(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Tous</option>
                    <option value="justified">Justifié</option>
                    <option value="unjustified">Non Justifié</option>
                </select>
            </div>
            <div>
                <label htmlFor="filterClasseId" className="block text-sm font-medium text-gray-700">Classe</label>
                <select 
                  id="filterClasseId" 
                  value={filterClasseId} 
                  onChange={(e) => setFilterClasseId(e.target.value)} 
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-sm"
                  disabled={userRole === 'enseignant' && teacherAssignedClasses.length === 0}
                >
                    <option value="">Toutes</option>
                    {userRole === 'enseignant' ? (
                      teacherAssignedClasses.length === 0 ? (
                        <option value="" disabled>Aucune classe attribuée</option>
                      ) : (
                        classes
                          .filter(c => teacherAssignedClasses.includes(c.id))
                          .map(c => (
                            <option key={c.id} value={c.id}>{c.nom}</option>
                          ))
                      )
                    ) : (
                      classes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))
                    )}
                </select>
            </div>
            <div>
                <label htmlFor="filterEleveId" className="block text-sm font-medium text-gray-700">Élève</label>
                <select 
                  id="filterEleveId" 
                  value={filterEleveId} 
                  onChange={(e) => setFilterEleveId(e.target.value)} 
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                    <option value="">Tous</option>
                    {eleves
                      .filter(e => userRole !== 'enseignant' || teacherAssignedClasses.length === 0 || teacherAssignedClasses.includes(e.classeId))
                      .map(e => (
                        <option key={e.id} value={e.id}>{e.name} ({e.className})</option>
                    ))}
                </select>
            </div>
        </div>
      )}

      {/* Tableau des Enregistrements */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Élève (Classe)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Heure</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Adm.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signalé par</th>
                  {canManage && (
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={canManage ? 7 : 6} className="px-6 py-4 text-center text-sm text-gray-500 italic">
                      {isLoading ? 'Chargement...' : 
                       userRole === 'enseignant' && teacherAssignedClasses.length === 0 ? 
                       'Vous n\'êtes pas affecté à des classes.' : 
                       'Aucun suivi trouvé pour les filtres sélectionnés.'}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map(record => (
                    <tr 
                      key={record.id} 
                      className={record.type === 'absence' ? 'bg-red-50 hover:bg-red-100' : 'bg-yellow-50 hover:bg-yellow-100'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.eleveName}
                        <span className="text-xs text-gray-500 block">({record.className || 'N/A'})</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          record.type === 'absence' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {record.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateForDisplay(record.date)}
                        {record.heure && <span className="block font-semibold">{record.heure}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {record.isJustified ? (
                          <span className="flex items-center text-green-600" title={record.justification}>
                            <CheckCircle size={16} className="mr-1" /> Justifié
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600">
                            <XCircle size={16} className="mr-1" /> Non Justifié
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={record.actionEntreprise}>
                        {record.actionEntreprise || 'Aucune action.'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.reportedBy || 'Système'}
                      </td>
                      {canManage && (
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <button
                            onClick={() => handleEdit(record)}
                            className="text-indigo-600 hover:text-indigo-900 mr-4 p-2 rounded-full hover:bg-indigo-100 transition duration-150"
                            title="Modifier"
                            disabled={userRole === 'enseignant' && !canAccessClass(record.classeId)}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(record.id)}
                            className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100 transition duration-150"
                            title="Supprimer"
                            disabled={userRole === 'enseignant' && !canAccessClass(record.classeId)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>
      
      {/* Modale d'ajout/modification (Individuel) */}
      {renderRecordModal()}
      
      {/* Modale Faire l'Appel (Batch) */}
      {renderRollCallModal()}
    </div>
  );
};

export default RetardsAbsencesManagement;